import * as Api from '@ViewModels';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useGetSystemJobQuery } from '../../../../../queries/SystemJob';
import { usePeopleSearch } from '../PeopleContext';

// Hook to manage action bar status based on context
export const useActionBarStatus = () => {
	// Use the context-based approach
	const { selectionState, selectedContactIds, excludedContactIds } = usePeopleSearch();

	// Calculate action bar status based on context
	const isActive = selectionState !== Api.ESelectionState.None;
	const canKeepInTouch = selectedContactIds.length > 0;
	const canMerge = selectedContactIds.length > 1;
	const canFocus = isActive;
	const canRestore = isActive || selectedContactIds.length >= 1;
	const isSelectingAllWithExcludedContacts =
		selectionState === Api.ESelectionState.Some && excludedContactIds.length > 0;
	const isSelectingAll = selectionState === Api.ESelectionState.All || isSelectingAllWithExcludedContacts;

	return {
		canKeepInTouch,
		canMerge,
		canRestore,
		canFocus,
		isActive,
		isSelectingAll,
	} as const;
};

// Hook to watch a system job until it completes, then fire a callback with the final job state.
// Generic — used for any bulk operation that returns an ISystemJob (visibility, SMS opt-out, etc.).
export const useSystemJob = (onJobComplete: (job: Api.ISystemJob) => void) => {
	const _userSession = useUserSession();
	const [jobId, setJobId] = useState<string>(null);
	const currentJobRef = useRef<string>(null);

	// Setup system job query
	const systemJobQuery = useGetSystemJobQuery({
		enabled: !!jobId,
		jobId,
		refetchInterval: query => {
			const data = query.state.data;
			if (data?.percentComplete >= 100) {
				return false;
			}
			return 1000; // Refetch every second until complete
		},
	});
	useEffect(() => {
		if (systemJobQuery.data) {
			if (systemJobQuery.data.percentComplete >= 100 && jobId === currentJobRef.current) {
				onJobComplete(systemJobQuery.data);
				setJobId(null);
				currentJobRef.current = null;
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [systemJobQuery.data]);

	// Start watching a system job
	const startWatchingJob = useCallback((systemJob: Api.ISystemJob) => {
		if (systemJob?.id) {
			setJobId(systemJob.id);
			currentJobRef.current = systemJob.id;
		}
	}, []);

	// Effects to clean up job monitoring on unmount
	useEffect(() => {
		return () => {
			// Clean up when component unmounts
			currentJobRef.current = null;
		};
	}, []);

	return {
		startWatchingJob,
		isJobRunning: !!jobId,
		job: systemJobQuery.data,
		jobId,
	};
};
